// Transaction Wizard — the scan parser, client side (design §6.4 / §11.1, §15).
//
// ONLINE, a scan is parsed by the SERVER (`wizard_scan`): the parse rules are
// read from the saved model, so the browser never decides what a code means
// and every surface shares one regex dialect and one GS1 implementation
// (`twasta_runtime_engine/wizard/scan.py` + `gs1.py`, a Java twin for J2EE).
//
// OFFLINE there is no server to ask. A host that runs disconnected says so
// (`isOffline()`), and only then does the runner parse here — with the rules of
// the model the device already holds, which is the same saved model the server
// would have read. This file is a port of the Python parser, held to it by the
// shared fixtures (`test-script/wizard_fixtures/scan/cases.json`, run by
// `test-script/test_wizard_scan_parity.mjs`). A behaviour change is Python +
// Java + this file + fixtures in one change.

import { ExprError, truthy } from '../expr'
import type {
  WizardFrameValues, WizardHost, WizardScanParse, WizardScanResponse, WizardSet, WizardStep,
} from './contract'
import { buildFormIndex, evaluateExpr, renderTemplate, type Row, type WizardScope } from './model'

/** Longest scanned payload accepted (QR codes top out near 4 KB). */
export const MAX_SCAN_CHARS = 4096
export const SCAN_FORMATS = ['gs1', 'json', 'kv', 'delimited', 'url', 'raw'] as const
const DEFAULT_KV_SEPARATORS = ';&|\n'

// ── offline seam ─────────────────────────────────────────────────────────

/** Whether the host reports it is offline now (`WizardHost.isOffline`).
 *  Absent ⇒ online; a throwing probe reads as online (the server parses). */
export function isHostOffline(host: Pick<WizardHost, 'isOffline'> | null | undefined): boolean {
  if (!host?.isOffline) return false
  try { return !!host.isOffline() } catch { return false }
}

// ── GS1 ──────────────────────────────────────────────────────────────────

export class Gs1Error extends Error {}

const GS = '\x1d'
const GS_ALIASES = ['<GS>', '<gs>', '{GS}']
const SYMBOLOGY_RE = /^\][A-Za-z][0-9A-Za-z]/
type Kind = 'n' | 'an' | 'date' | 'count' | 'dec' | 'money' | 'datetime'
type Spec = [number | null, number, Kind]

const FIXED: Record<string, Spec> = {
  '00': [18, 18, 'n'], '01': [14, 14, 'n'], '02': [14, 14, 'n'],
  '10': [null, 20, 'an'], '11': [6, 6, 'date'], '12': [6, 6, 'date'],
  '13': [6, 6, 'date'], '15': [6, 6, 'date'], '16': [6, 6, 'date'],
  '17': [6, 6, 'date'], '20': [2, 2, 'n'], '21': [null, 20, 'an'],
  '22': [null, 20, 'an'], '235': [null, 28, 'an'], '240': [null, 30, 'an'],
  '241': [null, 30, 'an'], '242': [null, 6, 'n'], '243': [null, 20, 'an'],
  '250': [null, 30, 'an'], '251': [null, 30, 'an'], '253': [null, 30, 'an'],
  '254': [null, 20, 'an'], '255': [null, 25, 'n'],
  '30': [null, 8, 'count'], '37': [null, 8, 'count'],
  '400': [null, 30, 'an'], '401': [null, 30, 'an'], '402': [17, 17, 'n'],
  '403': [null, 30, 'an'],
  '410': [13, 13, 'n'], '411': [13, 13, 'n'], '412': [13, 13, 'n'],
  '413': [13, 13, 'n'], '414': [13, 13, 'n'], '415': [13, 13, 'n'],
  '416': [13, 13, 'n'], '417': [13, 13, 'n'],
  '420': [null, 20, 'an'], '421': [null, 12, 'an'], '422': [3, 3, 'n'],
  '423': [null, 15, 'n'], '424': [3, 3, 'n'], '425': [null, 15, 'n'],
  '426': [3, 3, 'n'],
  '7003': [10, 10, 'datetime'], '8004': [null, 30, 'an'],
  '8005': [6, 6, 'n'], '8006': [18, 18, 'n'], '8008': [null, 12, 'datetime'],
  '8018': [18, 18, 'n'], '8020': [null, 25, 'an'],
  '90': [null, 30, 'an'],
}
for (let i = 91; i < 100; i++) FIXED[String(i)] = [null, 90, 'an']

const AI_LENGTH_BY_PREFIX: Record<string, number> = {}
for (const p of ['00', '01', '02', '10', '11', '12', '13', '15', '16', '17', '20', '21', '22', '30', '37',
  '90', '91', '92', '93', '94', '95', '96', '97', '98', '99']) AI_LENGTH_BY_PREFIX[p] = 2
for (const p of ['23', '24', '25', '40', '41', '42']) AI_LENGTH_BY_PREFIX[p] = 3
for (const p of ['31', '32', '33', '34', '35', '36', '39', '70', '80', '81', '82']) AI_LENGTH_BY_PREFIX[p] = 4

const isDigits = (s: string) => /^[0-9]+$/.test(s)

function spec(ai: string): Spec {
  if (ai in FIXED) return FIXED[ai]
  if (ai.length === 4 && /[0-9]/.test(ai[3])) {
    const fam = ai.slice(0, 3)
    if (fam >= '310' && fam <= '369') return [6, 6, 'dec']
    if (fam === '390' || fam === '392') return [null, 15, 'dec']
    if (fam === '391' || fam === '393') return [null, 18, 'money']
  }
  throw new Gs1Error(`Unknown GS1 Application Identifier (${ai})`)
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0')

function lastDay(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function gsDate(text: string, today: Date): string {
  if (!/^\d{6}$/.test(text)) throw new Gs1Error(`Not a GS1 date: '${text}'`)
  const yy = Number(text.slice(0, 2)); const mm = Number(text.slice(2, 4)); let dd = Number(text.slice(4, 6))
  if (mm < 1 || mm > 12) throw new Gs1Error(`Not a GS1 date: '${text}'`)
  const curCent = Math.floor(today.getFullYear() / 100)
  const curYy = today.getFullYear() % 100
  const diff = yy - curYy
  let century = curCent
  if (diff >= 51 && diff <= 99) century -= 1
  else if (diff >= -99 && diff <= -50) century += 1
  const year = century * 100 + yy
  const last = lastDay(year, mm)
  if (dd === 0) dd = last
  if (dd < 1 || dd > last) throw new Gs1Error(`Not a GS1 date: '${text}'`)
  return `${pad(year, 4)}-${pad(mm)}-${pad(dd)}`
}

function gsDateTime(text: string, today: Date): string {
  if (!/^\d{8}(\d{2}){0,2}$/.test(text)) throw new Gs1Error(`Not a GS1 date-time: '${text}'`)
  const day = gsDate(text.slice(0, 6), today)
  const hh = text.slice(6, 8)
  const mi = text.slice(8, 10) || '00'
  const ss = text.slice(10, 12)
  return `${day}T${hh}:${mi}${ss ? `:${ss}` : ''}`
}

function decimal(digits: string, places: number): number {
  if (!isDigits(digits)) throw new Gs1Error(`Not a GS1 number: '${digits}'`)
  const n = Number(digits)
  if (places === 0) return n
  return Number((n / 10 ** places).toFixed(places))
}

function convert(ai: string, raw: string, out: Row, today: Date): void {
  const [fixed, maxLen, kind] = spec(ai)
  if (fixed !== null && raw.length !== fixed) throw new Gs1Error(`(${ai}) must be ${fixed} characters, got ${raw.length}`)
  if (raw.length > maxLen || !raw) throw new Gs1Error(`(${ai}) has an invalid length (${raw.length})`)
  if (kind !== 'an' && !isDigits(raw)) throw new Gs1Error(`(${ai}) must be numeric`)
  const fam = ai.slice(0, 3)
  switch (kind) {
    case 'date': out[ai] = gsDate(raw, today); break
    case 'datetime': out[ai] = gsDateTime(raw, today); break
    case 'count': out[ai] = Number(raw); break
    case 'dec':
      out[ai] = decimal(raw, Number(ai[3]))
      if (!(fam in out)) out[fam] = out[ai]
      break
    case 'money':
      if (raw.length < 4) throw new Gs1Error(`(${ai}) must carry a currency and an amount`)
      out[`${ai}_currency`] = raw.slice(0, 3)
      out[ai] = decimal(raw.slice(3), Number(ai[3]))
      if (!(fam in out)) out[fam] = out[ai]
      break
    default: out[ai] = raw
  }
}

function clean(text: string): string {
  let t = String(text ?? '')
  for (const a of GS_ALIASES) t = t.split(a).join(GS)
  t = t.replace(/^[ \t\r\n]+|[ \t\r\n]+$/g, '')
  t = t.replace(SYMBOLOGY_RE, '')
  return t.replace(new RegExp(`^${GS}+`), '')
}

function parseHri(text: string, today: Date): Row {
  const out: Row = {}
  const re = /\((\d{2,4})\)/g
  const matches: RegExpExecArray[] = []
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) matches.push(m)
  if (!matches.length || matches[0].index !== 0) throw new Gs1Error('A human-readable GS1 string starts with (AI)')
  let pos = 0
  matches.forEach((mm, i) => {
    if (mm.index !== pos) throw new Gs1Error('Unexpected text between Application Identifiers')
    const end = i + 1 < matches.length ? matches[i + 1].index : text.length
    const raw = text.slice(mm.index + mm[0].length, end).split(GS).join('')
    convert(mm[1], raw, out, today)
    pos = end
  })
  return out
}

function parseRaw(text: string, today: Date): Row {
  const out: Row = {}
  let pos = 0
  const n = text.length
  while (pos < n) {
    if (text[pos] === GS) { pos += 1; continue }
    const prefix = text.slice(pos, pos + 2)
    const aiLen = AI_LENGTH_BY_PREFIX[prefix]
    if (aiLen === undefined || !isDigits(prefix)) throw new Gs1Error(`No GS1 Application Identifier at position ${pos}`)
    const ai = text.slice(pos, pos + aiLen)
    if (ai.length !== aiLen || !isDigits(ai)) throw new Gs1Error(`Truncated Application Identifier at position ${pos}`)
    const [fixed] = spec(ai)
    pos += aiLen
    let raw: string
    if (fixed !== null) {
      raw = text.slice(pos, pos + fixed)
      pos += fixed
    } else {
      let end = text.indexOf(GS, pos)
      end = end < 0 ? n : end
      raw = text.slice(pos, end)
      pos = end
    }
    convert(ai, raw, out, today)
  }
  if (!Object.keys(out).length) throw new Gs1Error('No GS1 data')
  return out
}

/** `{ai: value}` for a GS1 element string, human-readable or raw. Throws
 *  Gs1Error when it is not one. `today` pins the century window (tests). */
export function parseGs1(text: string, today?: Date): Row {
  const t = clean(text)
  if (!t) throw new Gs1Error('Empty code')
  const day = today || new Date()
  return t.startsWith('(') ? parseHri(t, day) : parseRaw(t, day)
}

// ── parse rules ──────────────────────────────────────────────────────────

/** A model regex: JavaScript `(?<name>…)` and Python `(?P<name>…)` both
 *  accepted (Python's spelling rewritten). Throws on a broken pattern. */
export function compilePattern(pattern: string): RegExp {
  return new RegExp(String(pattern || '').replace(/\(\?P<([A-Za-z_][A-Za-z0-9_]*)>/g, '(?<$1>'))
}

function flatten(obj: Row, prefix = ''): Row {
  const out: Row = {}
  for (const [k, v] of Object.entries(obj)) {
    const key = `${prefix}${k}`
    if (v && typeof v === 'object' && !Array.isArray(v)) Object.assign(out, flatten(v as Row, `${key}.`))
    else if (Array.isArray(v)) continue
    else out[key] = v
  }
  return out
}

function kv(text: string, separators: string): Row | null {
  const seps = separators || DEFAULT_KV_SEPARATORS
  const splitter = new RegExp(`[${seps.replace(/[\\\]^-]/g, '\\$&')}]`)
  const segs = text.split(splitter).map(s => s.trim()).filter(Boolean)
  if (!segs.length) return null
  const out: Row = {}
  for (const seg of segs) {
    const at = seg.indexOf('=')
    if (at < 0) return null
    const k = seg.slice(0, at).trim().toLowerCase()
    const v = seg.slice(at + 1).trim()
    if (!k || !v) return null
    out[k] = v
  }
  return out
}

function parseUrl(body: string, params: boolean): Row | null {
  const m = /^([A-Za-z][A-Za-z0-9+.-]*):\/\/([^/?#]*)([^?#]*)(?:\?([^#]*))?/.exec(body)
  if (!m) return null
  const scheme = m[1].toLowerCase()
  if ((scheme !== 'http' && scheme !== 'https') || !m[2]) return null
  const hostPort = m[2].replace(/^[^@]*@/, '')
  const host = (hostPort.startsWith('[') ? hostPort.slice(1, hostPort.indexOf(']')) : hostPort.split(':')[0]).toLowerCase()
  const out: Row = { url: body, host, path: m[3] || '' }
  if (params && m[4]) {
    for (const part of m[4].split(/[&;]/)) {
      if (!part) continue
      const at = part.indexOf('=')
      const dec = (s: string) => { try { return decodeURIComponent(s.replace(/\+/g, ' ')) } catch { return s } }
      const k = dec(at < 0 ? part : part.slice(0, at))
      const v = at < 0 ? '' : dec(part.slice(at + 1))
      if (!(k in out)) out[k] = v
    }
  }
  return out
}

/** Fields for `text` under one parse rule, or null when it does not match.
 *  Throws Error for a rule that is itself broken. */
export function parseRule(rule: WizardScanParse | null | undefined, text: string): Row | null {
  const r = (rule && typeof rule === 'object' ? rule : {}) as WizardScanParse
  const body = text.trim()
  const pattern = String(r.pattern || '').trim()
  if (pattern) {
    let rx: RegExp
    try { rx = compilePattern(pattern) } catch (err) {
      throw new Error(`The scan pattern does not compile: ${err instanceof Error ? err.message : String(err)}`)
    }
    const m = rx.exec(body)
    if (!m) return null
    const out: Row = {}
    for (const [k, v] of Object.entries(m.groups || {})) if (v !== undefined) out[k] = v
    return out
  }
  const fmt = String(r.format || 'raw').trim().toLowerCase()
  if (!(SCAN_FORMATS as readonly string[]).includes(fmt)) throw new Error(`Unknown scan format '${fmt}'`)
  switch (fmt) {
    case 'raw': return {}
    case 'gs1':
      try { return parseGs1(body) } catch (err) { if (err instanceof Gs1Error) return null; throw err }
    case 'json': {
      if (!body.startsWith('{')) return null
      let data: unknown
      try { data = JSON.parse(body) } catch { return null }
      return data && typeof data === 'object' && !Array.isArray(data) ? flatten(data as Row) : null
    }
    case 'kv': return kv(body, String(r.separators || ''))
    case 'delimited': {
      const sep = String(r.sep || '|')
      const names = (r.fields || []).map(f => String(f).trim()).filter(Boolean)
      if (!names.length) throw new Error('A delimited scan rule needs its field names')
      const values = body.split(sep)
      if (values.length !== names.length) return null
      const out: Row = {}
      names.forEach((n, i) => { out[n] = values[i].trim() })
      return out
    }
    case 'url': return parseUrl(body, r.params !== false)
    default: return null
  }
}

type RuleEntry = { name: string; rule: WizardScanParse; map: Record<string, unknown> }

function rulesOf(step: WizardStep): RuleEntry[] {
  const stepMap = (step.map && typeof step.map === 'object' ? step.map : {}) as Record<string, unknown>
  const alts = step.alternatives
  if (Array.isArray(alts) && alts.length) {
    const out: RuleEntry[] = []
    alts.forEach((alt, i) => {
      if (!alt || typeof alt !== 'object') return
      const name = String(alt.name || '').trim() || String(i)
      const amap = (alt.map && typeof alt.map === 'object' ? alt.map : stepMap) as Record<string, unknown>
      out.push({ name, rule: alt, map: amap })
    })
    return out
  }
  const parse = (step.parse && typeof step.parse === 'object' ? step.parse : { format: 'raw' }) as WizardScanParse
  return [{ name: String(parse.name || '').trim() || '0', rule: parse, map: stepMap }]
}

/** The scan fields as templates read them: flat keys (`meta.lot`, `3102`)
 *  plus their dotted paths nested, so `{scan.meta.lot}` resolves. */
function scanScope(fields: Row): Row {
  const out: Row = { ...fields }
  for (const [k, v] of Object.entries(fields)) {
    if (!k.includes('.')) continue
    const parts = k.split('.')
    let cur = out
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i]
      if (cur[p] === undefined) cur[p] = {}
      if (!cur[p] || typeof cur[p] !== 'object') { cur = {}; break }
      cur = cur[p] as Row
    }
    if (cur[parts[parts.length - 1]] === undefined) cur[parts[parts.length - 1]] = v
  }
  return out
}

/** Parse `text` by `step`'s rules in a wizard scope — the client twin of
 *  `scan.parse_scan`, same answer shape as `wizard_scan`. */
export function parseScanInScope(step: WizardStep, text: unknown, scope: WizardScope): WizardScanResponse {
  const raw = (text == null ? '' : String(text)).slice(0, MAX_SCAN_CHARS)
  const out: WizardScanResponse = { ok: false, rule: null, fields: {}, sets: [], expect_ok: false, message: '' }
  if (!raw.trim()) { out.message = 'Nothing was scanned.'; return out }
  let matched: { name: string; fields: Row; map: Record<string, unknown> } | null = null
  for (const { name, rule, map } of rulesOf(step || ({} as WizardStep))) {
    let fields: Row | null
    try { fields = parseRule(rule, raw) } catch (err) {
      out.message = err instanceof Error ? err.message : String(err)
      return out
    }
    if (fields !== null) { matched = { name, fields, map }; break }
  }
  if (!matched) {
    out.message = String((step as { no_match_message?: string })?.no_match_message || 'This code was not recognised.')
    return out
  }
  const fields: Row = { ...matched.fields }
  if (!('value' in fields)) fields.value = raw.trim()
  fields.rule = matched.name
  out.rule = matched.name
  out.fields = fields

  const sc: WizardScope = { ...scope, scan: scanScope(fields) }
  const sets: WizardSet[] = []
  for (const [target, tmpl] of Object.entries(matched.map || {})) {
    const r = renderTemplate(tmpl, sc)
    if (r.missing.length) continue
    sets.push({ target, value: r.value })
  }
  out.sets = sets

  const expect = step?.expect
  if (expect == null || (typeof expect === 'string' && !expect.trim())) {
    out.expect_ok = true
  } else {
    try {
      out.expect_ok = truthy(evaluateExpr(String(expect), sc))
    } catch (err) {
      out.expect_ok = false
      out.message = `The scan check could not be evaluated: ${err instanceof ExprError || err instanceof Error ? err.message : String(err)}`
    }
  }
  out.ok = !!out.expect_ok
  if (!out.ok && !out.message) {
    out.message = String((step as { expect_message?: string })?.expect_message || 'This is not the expected code.')
  }
  return out
}

/** The same over `WizardFrameValues` (the `wizard_scan` request's frame) —
 *  what the parity fixtures exercise. Columns resolve by name, case-insensitively. */
export function parseScan(step: WizardStep, text: unknown, frame: WizardFrameValues | null | undefined): WizardScanResponse {
  const fi = buildFormIndex(null)
  const f = frame || { header: {} }
  const scope: WizardScope = {
    fi,
    header: (f.header || {}) as Row,
    details: (f.lines || {}) as Record<string, Row[]>,
    line: (f.line || null) as Row | null,
    lineForm: fi.lineForm,
    vars: (f.vars || {}) as Row,
    input: (f.input || {}) as Row,
    steps: {},
    run: (f.run || {}) as Row,
  }
  return parseScanInScope(step, text, scope)
}
